import { Icon, usePrompt, useToast } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import type {
  AllIssueDataForSettingsQuery,
  IssueRiskAspectNode,
} from '@/modules/issues'
import {
  ALL_ISSUE_DATA_FOR_SETTINGS_QUERY,
  CREATE_SUBCATEGORY,
  PATCH_SUBCATEGORY,
} from '@/modules/issues'
import type { ShallowUserNode } from '@/modules/users'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { isMobileOnly } from 'react-device-detect'
import styled from 'styled-components'
import { useConfirm, useOnErrorAuto } from '@/utils/hooks'
import type {
  IssueCategoriesSettingsForm,
  IssueCategoryForSettings,
} from '../components'
import { IssueCategorySettingsCard } from '../components'
import {
  OldAddButton,
  InfoBox,
  OldSection as BaseSection,
  SectionHeader,
  SettingsWrapper,
} from '../components/common'
import type { IssueCategoryForm } from '../components/IssueCategorySettings'
import {
  IssueCategoryPrompt,
  IssueCategorySettingsTable,
} from '../components/IssueCategorySettings'
import { SelectUserPrompt } from '../components/SelectUserPrompt'
import {
  useIssueCategoryMutations,
  useIssueRiskAspectMutations,
} from '../util/mutations.hooks'
import { DropdownNames } from '@/components_deprecated/Select/types'
import type { IssueRiskAspectForm } from '../components/IssueCategorySettings/IssueRiskAspectPrompt'
import { IssueRiskAspectPrompt } from '../components/IssueCategorySettings/IssueRiskAspectPrompt'
import { Table } from '@/components_deprecated/Table'
import { Module } from '@/modules/companies/consts'
import { ModuleOptionCards } from '../components/ModuleOptionCards'

export const LS_KEY_LIST_VIEW_MODE_ISSUE_CATEGORIES_SETTINGS =
  'firmadok-list-view-mode-issue-categories-settings'

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`
const Section = styled(BaseSection)`
  h4 {
    margin: 0;
  }

  ul {
    margin: 0;
    padding-left: 1rem;
  }
`

interface IssueSettingsProps {}

export const IssueSettings: React.FC<IssueSettingsProps> = () => {
  const translations = useTranslate({
    info1: 'settings.info.issue-categories-1',
    info2: 'settings.info.issue-categories-2',

    issueCategories: 'common.issue-categories',
    issueRiskAspect: 'common.risk-aspect',
    issueRiskAspects: 'common.risk-aspects',

    validation: {
      nameExists: 'settings.validation.issue-category-name-exists',
    },
    moduleOptions: 'settings.module-options',
    prompt: {
      deleteCategory: ['settings.prompts.issue-category-delete', { name: '' }],
      deleteCategoryTitle: 'settings.prompts.issue-category-delete-title',
      deleteRiskAspect: [
        'settings.prompts.issue-risk-aspect-delete',
        { name: '' },
      ],
      deleteRiskAspectTitle: 'settings.prompts.issue-risk-aspect-delete-title',
    },
  })

  const confirm = useConfirm()

  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [temporaryEditColor, setTemporaryEditColor] = useState<{
    [id: string]: string | null
  }>({})

  const {
    formValues: form,
    formChangeHandler: handler,
    updateForm,
  } = useForm<IssueCategoriesSettingsForm>({
    values: {
      newName: '',
      newColor: 'hsl(138deg, 80%, 80%)',
      editName: '',
      defaultSeverity: 'MEDIUM',
      responsibleUser: null,
    },
  })

  // TODO fix exhaustive-deps?
  // eslint-disable-next-line
  const updateFormCallback = useCallback(updateForm, [])
  const onErrorAuto = useOnErrorAuto()
  const addToast = useToast()
  const addPrompt = usePrompt()

  const {
    data: queryData,
    loading: queryLoading,
    refetch,
  } = useQuery<AllIssueDataForSettingsQuery, never>(
    ALL_ISSUE_DATA_FOR_SETTINGS_QUERY,
    {
      onError: onErrorAuto(),
    }
  )

  const [editableCategories, uneditableCategories] = useMemo(
    () =>
      queryData?.allIssueCategories.edges.reduce<
        [IssueCategoryForSettings[], IssueCategoryForSettings[]]
      >(
        (acc, { node: category }) =>
          category.editable
            ? [[...acc[0], category], acc[1]]
            : [acc[0], [...acc[1], category]],
        [[], []]
      ) ?? [[], []],
    [queryData]
  )

  const riskAspects =
    queryData?.allIssueRiskAspects?.edges.map(edge => edge.node) ?? []

  useEffect(() => {
    if (!adding) {
      updateFormCallback({
        newName: '',
        newColor: 'hsl(138deg, 80%, 80%)',
        responsibleUser: null,
      })
    }
  }, [adding, updateFormCallback])

  useEffect(() => {
    if (editing === null)
      updateFormCallback({ editName: '', responsibleUser: null })
  }, [editing, updateFormCallback, editableCategories])

  const mutations = useIssueCategoryMutations()
  const riskAspectMutations = useIssueRiskAspectMutations()

  function validateName(name: string) {
    if (name === '') return false
    if (
      [...editableCategories, ...uneditableCategories].find(
        type => type.name === form.newName
      )
    ) {
      addToast('warning', translations.validation.nameExists)
      return false
    }
    return true
  }

  function checkNameHasChanged(id: string) {
    const category = editableCategories.find(type => type.id === id)
    if (typeof category === 'undefined') return true

    return category.name !== form.editName
  }

  async function submitNewIssueCategory() {
    if (!validateName(form.newName)) return

    await mutations.create({
      variables: {
        input: {
          name: form.newName,
          color: form.newColor,
          defaultSeverity: form.defaultSeverity,
          responsibleUser: form.responsibleUser?.id,
        },
      },
    })
    setAdding(false)
  }

  async function submitEditIssueCategoryName() {
    if (!validateName(form.editName) || editing === null) return
    if (!checkNameHasChanged(editing)) {
      setEditing(null)
      return
    }

    await mutations.patch(true)({
      variables: {
        id: editing,
        input: { name: form.editName },
      },
    })
    setEditing(null)
  }

  function handleUpdateColor(id: string) {
    return async (color: string) => {
      setTemporaryEditColor(v => ({
        ...v,
        [id]: color,
      }))

      try {
        await mutations.patch(false)({
          variables: { id, input: { color } },
        })
      } finally {
        setTemporaryEditColor(v => ({
          ...v,
          [id]: null,
        }))
      }
    }
  }

  async function handleDeleteCategoryPrompt(
    category: IssueCategoryForSettings
  ) {
    const { data: answer } = await confirm(
      translations.prompt.deleteCategory({ name: category.name }),
      translations.prompt.deleteCategoryTitle
    )
    if (!answer) return

    void mutations.delete({
      variables: {
        id: category.id,
      },
    })
  }

  async function handleDeleteRiskAspectPrompt(riskAspect: IssueRiskAspectNode) {
    const { data: answer } = await confirm(
      translations.prompt.deleteRiskAspect({ name: riskAspect.name }),
      translations.prompt.deleteRiskAspectTitle
    )
    if (!answer) return

    void riskAspectMutations.delete({
      variables: {
        id: riskAspect.id,
      },
    })
  }

  async function handleResponsibleUserPrompt(
    categoryId: string | null,
    responsibleUser: ShallowUserNode | null
  ) {
    updateForm({ responsibleUser })

    const { data: user } = await addPrompt<typeof responsibleUser>(resolve => (
      <SelectUserPrompt
        user={responsibleUser}
        dropdownPageName={DropdownNames.ISSUES}
        onResolve={resolve}
      />
    ))
    if (user === null) return

    if (user.id === responsibleUser?.id) {
      updateForm({ responsibleUser: null })
      setEditing(null)
      return
    }

    if (categoryId === null) updateForm({ responsibleUser: user })
    else void handleSubmitResponsibleUser(categoryId, user)
  }

  async function handleSubmitResponsibleUser(
    categoryId: string,
    responsibleUser: ShallowUserNode
  ) {
    await mutations.patch(true)({
      variables: {
        id: categoryId,
        input: {
          responsibleUser: responsibleUser.id,
        },
      },
    })
    updateForm({ responsibleUser: null })
    setEditing(null)
  }

  const isLoading =
    queryLoading || mutations.loading || riskAspectMutations.loading

  const [createSubcategory] = useMutation(CREATE_SUBCATEGORY)
  const [patchSubcategory] = useMutation(PATCH_SUBCATEGORY)

  async function handleCreateCategoryPrompt() {
    const { data } = await addPrompt<IssueCategoryForm | null>(resolve => (
      <IssueCategoryPrompt
        editableCategories={editableCategories}
        uneditableCategories={uneditableCategories}
        onSubmit={resolve}
      />
    ))
    if (data === null || data.responsibleUser === null) return
    const { data: categoryResp } = await mutations.create({
      variables: {
        input: {
          name: data.name,
          color: data.color,
          responsibleUser: data.responsibleUser,
          automaticReminderInterval: data.automaticReminderInterval,
          minimumSeverityForReminder: data.minimumSeverityForReminder,
          remindResponsible: data.remindResponsible,
          remindCreator: data.remindCreator,
        },
      },
    })
    const categoryId = categoryResp?.createIssueCategory?.issueCategory?.id
    if (!categoryId) return

    await Promise.all(
      data?.subcategories?.map(subcategory =>
        createSubcategory({
          variables: {
            input: {
              category: categoryId,
              name: subcategory.name,
              defaultSeverity: subcategory.defaultSeverity,
              responsibleUser: data.responsibleUser,
            },
          },
        })
      )
    )
    await refetch()
  }

  async function handleCreateRiskAspectPrompt() {
    const { data } = await addPrompt<IssueRiskAspectForm | null>(resolve => (
      <IssueRiskAspectPrompt
        existingRiskAspects={riskAspects}
        onSubmit={resolve}
      />
    ))
    if (data === null) return

    void riskAspectMutations.create({
      variables: {
        input: { ...data },
      },
    })
  }

  async function handleEditCategoryPrompt(category: IssueCategoryForSettings) {
    const initialData: IssueCategoryForm = {
      name: category.name,
      color: category.color,
      responsibleUser: category.responsibleUser?.id ?? null,
      automaticReminderInterval: category.automaticReminderInterval,
      minimumSeverityForReminder: category.minimumSeverityForReminder,
      remindResponsible: category.remindResponsible,
      remindCreator: category.remindCreator,
      subcategories:
        category.issueSubcategories?.edges?.map(el => el.node) ?? [],
      newSubCategory: {
        name: '',
        defaultSeverity: 'MEDIUM',
      },
    }
    const { data } = await addPrompt<IssueCategoryForm | null>(resolve => (
      <IssueCategoryPrompt
        category={category}
        initialData={initialData}
        editableCategories={editableCategories}
        uneditableCategories={uneditableCategories}
        onSubmit={resolve}
      />
    ))
    if (data === null || data.responsibleUser === null) return

    const { data: categoryResp } = await mutations.patch(true)({
      variables: {
        id: category.id,
        input: {
          name: data.name,
          color: data.color,
          responsibleUser: data.responsibleUser,
          automaticReminderInterval: data.automaticReminderInterval,
          minimumSeverityForReminder: data.minimumSeverityForReminder,
          remindResponsible: data.remindResponsible,
          remindCreator: data.remindCreator,
        },
      },
    })
    const categoryId = categoryResp?.patchIssueCategory?.issueCategory?.id
    if (!categoryId) return

    await Promise.all(
      data?.subcategories?.map(subcategory => {
        if (subcategory?.id) {
          void patchSubcategory({
            variables: {
              id: subcategory.id,
              input: {
                name: subcategory.name,
                category: categoryId,
                defaultSeverity: subcategory.defaultSeverity,
                responsibleUser: data.responsibleUser,
              },
            },
          })
        } else {
          void createSubcategory({
            variables: {
              input: {
                category: categoryId,
                name: subcategory.name,
                defaultSeverity: subcategory.defaultSeverity,
                responsibleUser: data.responsibleUser,
              },
            },
          })
        }
        return true
      })
    )
    await refetch()
  }

  async function handleEditRiskAspectPrompt(riskAspect: IssueRiskAspectNode) {
    const initialData: IssueRiskAspectForm = {
      name: riskAspect.name,
    }
    const { data } = await addPrompt<IssueRiskAspectForm | null>(resolve => (
      <IssueRiskAspectPrompt
        initialData={initialData}
        existingRiskAspects={riskAspects}
        onSubmit={resolve}
      />
    ))
    if (data === null) return

    void riskAspectMutations.patch({
      variables: {
        id: riskAspect.id,
        input: data,
      },
    })
  }

  return (
    <SettingsWrapper grid={{ flow: 'row' }}>
      <InfoBox initCollapsed={isMobileOnly}>
        <p>{translations.info1}</p>
        <p>{translations.info2}</p>
      </InfoBox>
      <Section>
        <Header>
          <SectionHeader loading={isLoading}>
            {translations.issueRiskAspects}
          </SectionHeader>
          {!isMobileOnly && (
            <OldAddButton
              adding={adding}
              onClick={handleCreateRiskAspectPrompt}
            />
          )}
        </Header>
        {riskAspects.length > 0 && (
          <Table
            noShadow
            noBorders
            noHeaderMargin
            noRowMargin
            rowHoverColor="secondary"
          >
            <thead>
              <tr>
                <th>{translations.issueRiskAspect}</th>
                <th colSpan={2} />
              </tr>
            </thead>
            <tbody>
              {riskAspects.map(aspect => (
                <tr key={aspect.id}>
                  <td>{aspect.name}</td>
                  <td width="1px">
                    <Icon
                      icon="edit"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="secondary"
                      onClick={() => handleEditRiskAspectPrompt(aspect)}
                    />
                  </td>
                  <td width="1px">
                    <Icon
                      icon="times"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="red"
                      onClick={() => handleDeleteRiskAspectPrompt(aspect)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>
      <Section>
        <Header>
          <SectionHeader>{translations.moduleOptions}</SectionHeader>
        </Header>
        <ModuleOptionCards module={Module.ISSUES} />
      </Section>
      <Section>
        <Header>
          <SectionHeader loading={isLoading}>
            {translations.issueCategories}
          </SectionHeader>
          {!isMobileOnly && (
            <OldAddButton
              adding={adding}
              onClick={handleCreateCategoryPrompt}
            />
          )}
        </Header>

        {isMobileOnly ? (
          <IssueCategorySettingsCard
            editableCategories={editableCategories}
            uneditableCategories={uneditableCategories}
            form={form}
            adding={adding}
            editing={editing}
            temporaryEditColor={temporaryEditColor}
            onFormChange={handler}
            onUpdateForm={updateForm}
            onSelectResponsibleUser={handleResponsibleUserPrompt}
            onUpdateColor={handleUpdateColor}
            onAdd={setAdding}
            onAddSubmit={submitNewIssueCategory}
            onEdit={setEditing}
            onEditSubmit={submitEditIssueCategoryName}
            onDelete={handleDeleteCategoryPrompt}
          />
        ) : (
          <IssueCategorySettingsTable
            editableCategories={editableCategories}
            uneditableCategories={uneditableCategories}
            form={form}
            temporaryEditColor={temporaryEditColor}
            onSelectResponsibleUser={handleResponsibleUserPrompt}
            onUpdateColor={handleUpdateColor}
            onEditClick={handleEditCategoryPrompt}
            onDelete={handleDeleteRiskAspectPrompt}
          />
        )}
      </Section>
    </SettingsWrapper>
  )
}
